import React from "react";
import { Icon, EmptyState, notify, confirmDialog } from "./registry";
/* ============================================================
   Fence Works — FW Admin → Deletion Requests (platform owner only)
   The operator's side of docs/ACCOUNT_DELETION.md, in the app instead of
   curl: the queue of workspaces that asked to be deleted, decline with a
   note, or run the purge — the one irreversible action in the product, so
   the workspace id has to be typed back before the button does anything.
   Owner-gated on the service (SUPPORT_OWNER_EMAILS, verified email); the
   server 403s anyone else and this page says so.
   ============================================================ */

const opsApi = (): any => (window as any).VFP_DB && (window as any).VFP_DB.ops;
const STATUSES: [string, string][] = [["open", "Open"], ["completed", "Completed"], ["cancelled", "Declined"], ["failed", "Failed"]];
const when = (at: any) => { const d = new Date(at); return isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };

function DeletionRequestsPage({ go }: any) {
  const api = opsApi();
  const live = !!(api && api.enabled && api.enabled());
  const [status, setStatus] = React.useState<any>("open");
  const [rows, setRows] = React.useState<any[]>([]);
  const [setup, setSetup] = React.useState<any>(true);
  const [loading, setLoading] = React.useState<any>(true);
  const [denied, setDenied] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(null);
  const [notes, setNotes] = React.useState<any>({});
  const [results, setResults] = React.useState<any>({});   // request id → purge response, kept on screen

  const load = React.useCallback(() => {
    if (!live) { setLoading(false); return; }
    setErr("");
    api.deletions(status).then((d: any) => { setRows(d.requests || []); setSetup(d.setup !== false); setLoading(false); setDenied(false); })
      .catch((e: any) => { setLoading(false); if (/403|forbidden/i.test(String(e.message || e))) setDenied(true); else setErr(String(e.message || e)); });
  }, [live, status]);
  React.useEffect(() => { setLoading(true); load(); }, [load]);

  async function decline(r: any) {
    const note = String(notes[r.id] || "").trim();
    const ok = await confirmDialog({
      title: "Decline this request?",
      body: (r.company_name || r.requester_email || r.workspace_id) + " keeps their account and every row of data. " + (note ? "Your note goes on the record." : "No note — add one first if this was a conversation worth remembering."),
      confirmLabel: "Decline — nothing is deleted",
    });
    if (!ok) return;
    setBusy("decline:" + r.id); setErr("");
    try { await api.declineDeletion(r.id, note); notify("Declined. Nothing was deleted."); load(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(null); }
  }

  async function purge(r: any) {
    const ok = await confirmDialog({
      title: "Purge this workspace for good?",
      body: "Releases their Twilio number, A2P campaign and Retell agent first, then wipes every row and their sign-in. There is no undo and no backup restore. Type the workspace id exactly to continue.",
      confirmLabel: "Purge workspace",
      danger: true,
      requireText: String(r.workspace_id),
      requireHint: <span>Type <code className="mono">{r.workspace_id}</code></span>,
    });
    if (!ok) return;
    setBusy("purge:" + r.id); setErr("");
    try {
      const out = await api.purgeWorkspace(r.id, String(r.workspace_id));
      setResults((x: any) => ({ ...x, [r.id]: out }));
      const manual = (out && out.manual) || [];
      notify(manual.length ? manual.length + " thing" + (manual.length === 1 ? "" : "s") + " to release by hand — see below." : "Purged. Nothing left to do by hand.", { kind: manual.length ? "error" : "success" });
      load();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(null); }
  }

  if (!live) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="trash" title="Needs the live service" sub="Deletion requests live on the automation service (app.fenceworks.ai)." /></div>
    </div>
  );
  if (denied) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Owner only" sub="Deletion requests are limited to the platform owner's account (SUPPORT_OWNER_EMAILS, verified)." />
        <button className="btn btn-primary" onClick={() => go && go("dashboard")}>Back to dashboard</button></div>
    </div>
  );

  return (
    <div className="page fade-up" style={{ maxWidth: 920 }}>
      <div style={{ marginBottom: 4 }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Deletion Requests</h1>
        <p className="muted" style={{ margin: "4px 0 0" }}>Workspaces that asked for their account and data to be deleted. Talk first if it's worth talking about; a decline touches nothing. The purge is the only action in the product that cannot be undone.</p>
      </div>
      <div className="cat-tabs" style={{ margin: "14px 0 16px" }}>
        {STATUSES.map(([id, label]) => (
          <button key={id} className={"filter-chip" + (status === id ? " on" : "")} onClick={() => setStatus(id)}>{label}</button>
        ))}
      </div>
      {!setup && <p className="muted" style={{ color: "var(--amber)", fontSize: 13 }}>The deletion_requests table isn't on this database — run <code>migrate_2026-09-17_account_deletion.sql</code>. Nobody can ask until it is.</p>}
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}

      {loading
        ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 24 }}>Loading…</p>
        : rows.length === 0
          ? <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="check" title={status === "open" ? "Nothing waiting" : "Nothing here"} sub={status === "open" ? "No workspace is asking to be deleted." : "No " + (STATUSES.find(s => s[0] === status) || ["", ""])[1].toLowerCase() + " requests."} /></div>
          : <div style={{ display: "grid", gap: 12 }}>
              {rows.map((r: any) => {
                const res = results[r.id];
                const manual = (res && res.manual) || (r.teardown && r.teardown.manual) || [];
                const open = r.status === "open";
                return (
                  <div key={r.id} className="card pad" style={{ borderColor: manual.length ? "var(--amber)" : undefined }}>
                    <div className="row between" style={{ flexWrap: "wrap", gap: 8, alignItems: "baseline" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{r.company_name || r.requester_email || "Workspace"}</div>
                        <div className="muted" style={{ fontSize: 12.5 }}>{[r.requester_name, r.requester_email].filter(Boolean).join(" · ") || "no requester on record"}</div>
                      </div>
                      <div className="faint" style={{ fontSize: 11.5, textAlign: "right" }}>
                        <div>asked {when(r.created_at)}</div>
                        {!open && <div>{r.status} {r.settled_at ? when(r.settled_at) : ""}{r.operator_email ? " · by " + r.operator_email : ""}</div>}
                      </div>
                    </div>
                    <div className="mono faint" style={{ fontSize: 11, margin: "6px 0 0", wordBreak: "break-all" }}>workspace {r.workspace_id}</div>
                    {r.reason && <blockquote style={{ margin: "10px 0 0", padding: "8px 12px", borderLeft: "3px solid var(--teal)", background: "var(--surface-2)", borderRadius: 8, fontSize: 13, whiteSpace: "pre-wrap" }}>{r.reason}</blockquote>}

                    {open && (
                      <div style={{ borderTop: "1px solid var(--line)", marginTop: 12, paddingTop: 12 }}>
                        <textarea className="textarea" rows={2} placeholder="Note for the record (optional) — what you talked about, or why you're declining" value={notes[r.id] || ""} onChange={(e) => setNotes((n: any) => ({ ...n, [r.id]: e.target.value }))} style={{ width: "100%", marginBottom: 10 }} />
                        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                          <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => decline(r)}>{busy === "decline:" + r.id ? "Declining…" : "Decline — keep their account"}</button>
                          <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} disabled={!!busy} onClick={() => purge(r)}><Icon name="trash" size={13} />{busy === "purge:" + r.id ? "Purging…" : "Purge workspace"}</button>
                          {r.requester_email && <a className="btn btn-ghost btn-sm" href={"mailto:" + r.requester_email} style={{ marginLeft: "auto" }}><Icon name="mail" size={13} />Email them</a>}
                        </div>
                      </div>
                    )}

                    {manual.length > 0 && (
                      <div style={{ borderTop: "1px solid var(--line)", marginTop: 12, paddingTop: 12 }}>
                        <div className="kicker" style={{ color: "var(--amber)", marginBottom: 6 }}>Release these by hand</div>
                        <div style={{ display: "grid", gap: 4 }}>
                          {manual.map((m: any, i: number) => <div key={i} style={{ fontSize: 12.5 }}>{typeof m === "string" ? m : [m.what || m.step || m.resource, m.id || m.sid, m.todo || m.why || m.error].filter(Boolean).join(" — ")}</div>)}
                        </div>
                      </div>
                    )}
                    {res && res.summary && (
                      <details style={{ marginTop: 10 }}>
                        <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>What was deleted</summary>
                        <pre style={{ fontSize: 11, color: "var(--fg-3)", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", overflow: "auto", maxHeight: 220, margin: "6px 0 0", whiteSpace: "pre-wrap" }}>{JSON.stringify(res.summary, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>}
    </div>
  );
}

/* ============================================================
   FW Admin → Admins (platform admins)
   Who can open FW Admin. Addresses in SUPPORT_OWNER_EMAILS on the server are
   listed as locked (a server setting, and the way back in if this list is
   ever emptied); everyone else is a row any admin can add or remove. The
   service refuses removing yourself or the last admin; the buttons say so
   before anyone clicks. Needs migration #33 to add anyone.
   ============================================================ */
function PlatformAdminsPage({ go }: any) {
  const api = (window as any).VFP_DB && (window as any).VFP_DB.platformAdmins;
  const live = !!(api && api.enabled && api.enabled());
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<any>(true);
  const [denied, setDenied] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [email, setEmail] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(null);

  const load = React.useCallback(() => {
    if (!live) { setLoading(false); return; }
    setErr("");
    api.list().then((d: any) => { setData(d); setLoading(false); setDenied(false); })
      .catch((e: any) => { setLoading(false); if (e && e.status === 403) setDenied(true); else setErr(String((e && e.message) || e)); });
  }, [live]);
  React.useEffect(() => { load(); }, [load]);

  const admins: any[] = (data && data.admins) || [];
  const setup = !data || data.setup !== false;
  const lastOne = admins.length <= 1;

  async function add(e: any) {
    e && e.preventDefault && e.preventDefault();
    const v = String(email || "").trim();
    if (!v) return;
    setBusy("add"); setErr("");
    try {
      const out = await api.add(v);
      notify(out && out.already ? out.email + " is already an admin." : (out.email + " is now an admin." + (out.userFound ? "" : " They get in once they sign up with that address and confirm it.")));
      setEmail(""); load();
    } catch (x: any) { setErr(String((x && x.message) || x)); }
    finally { setBusy(null); }
  }
  async function remove(a: any) {
    const ok = await confirmDialog({
      title: "Remove " + a.email + "?",
      body: "They lose FW Admin on their next page load. Their own workspace isn't touched.",
      confirmLabel: "Remove admin",
      danger: true,
    });
    if (!ok) return;
    setBusy("rm:" + a.email); setErr("");
    try { await api.remove(a.email); notify(a.email + " is no longer an admin."); load(); }
    catch (x: any) { setErr(String((x && x.message) || x)); }
    finally { setBusy(null); }
  }
  const whyLocked = (a: any) => a.source === "env" ? "Set in SUPPORT_OWNER_EMAILS on the server — change it there"
    : a.you ? "You can't remove yourself — ask another admin"
    : lastOne ? "The last admin stays — add another first" : "";

  if (!live) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Needs the live service" sub="Platform admins live on the automation service (app.fenceworks.ai)." /></div>
    </div>
  );
  if (denied) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Owner only" sub="Limited to Fence Works platform admins (a confirmed email in SUPPORT_OWNER_EMAILS or on this list)." />
        <button className="btn btn-primary" onClick={() => go && go("dashboard")}>Back to dashboard</button></div>
    </div>
  );

  return (
    <div className="page fade-up" style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 14 }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Admins</h1>
        <p className="muted" style={{ margin: "4px 0 0" }}>Who can open FW Admin: every customer's account, their help chats, deletion requests and print orders. Add people by the email they sign in with; they need to have confirmed it.</p>
      </div>
      {!setup && <p className="muted" style={{ color: "var(--amber)", fontSize: 13 }}>The platform_admins table isn't on this database. Run migration #33 (<code>{(data && data.migration) || "migrate_2026-10-11_platform_admins_keys.sql"}</code>) to add admins here. Until then only the addresses in SUPPORT_OWNER_EMAILS are admins.</p>}
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}

      <form className="card pad row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }} onSubmit={add}>
        <div style={{ flex: "1 1 260px" }}>
          <label className="field-label" htmlFor="fwa-email">Add an admin</label>
          <input id="fwa-email" className="input" type="email" autoComplete="off" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!setup || !!busy} style={{ width: "100%" }} />
        </div>
        <button className="btn btn-primary" type="submit" disabled={!setup || !!busy || !String(email || "").trim()}><Icon name="plus" size={14} />{busy === "add" ? "Adding…" : "Add admin"}</button>
      </form>

      {loading
        ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 24 }}>Loading…</p>
        : <div className="card" style={{ overflow: "hidden" }}>
            {admins.map((a: any, i: number) => {
              const locked = whyLocked(a);
              return (
                <div key={a.email} className="row between" style={{ gap: 10, padding: "12px 16px", borderTop: i ? "1px solid var(--line)" : "none", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0, flex: "1 1 240px" }}>
                    <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                      <b style={{ fontSize: 14, wordBreak: "break-all" }}>{a.email}</b>
                      {a.you && <span className="chip" style={{ fontSize: 11 }}>You</span>}
                      {a.source === "env" && <span className="chip" style={{ fontSize: 11 }}><Icon name="lock" size={10} />Server setting</span>}
                    </div>
                    <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>
                      {a.source === "env" ? "SUPPORT_OWNER_EMAILS" : ["Added" + (a.added_by ? " by " + a.added_by : ""), a.added_at ? when(a.added_at) : ""].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" disabled={!!locked || !!busy} title={locked || "Remove this admin"} onClick={() => remove(a)} style={locked ? undefined : { color: "var(--red)" }}>
                    <Icon name={locked ? "lock" : "trash"} size={13} />{busy === "rm:" + a.email ? "Removing…" : "Remove"}
                  </button>
                </div>
              );
            })}
          </div>}
      <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Changes apply on the admin's next page load (within a minute everywhere). SUPPORT_OWNER_EMAILS stays the way back in if this list is ever emptied.</p>
    </div>
  );
}

Object.assign(window, { DeletionRequestsPage, PlatformAdminsPage });

export { DeletionRequestsPage, PlatformAdminsPage };
